import { html, LitElement, css, unsafeCSS } from 'lit';
import styles from './tabGroup.scss?inline';
import {
    ATTRIBUTES,
    DRAG_CLICK_SUPPRESS_PX,
    EVENTS,
    TAB_BAR_DRAGGING_CLASS,
    TAB_BAR_OVERFLOW_CLASS,
    TAB_BAR_OVERFLOW_EPSILON_PX,
    TAB_BAR_OVERFLOW_SYNC_DEBOUNCE_MS,
} from './constants';
import debounce from 'lodash/debounce';
import { ref, createRef } from 'lit/directives/ref.js';

/**
 * @attribute offset-element - When tabs act as anchors, CSS selector for an element whose height
 *   is subtracted from the scroll target so the tab group lines up below a fixed header (or similar).
 * @attribute disable-tab-bar-scroll - When set, clicking a tab does not scroll the tab bar.
 *   On first layout after load (including reload), the bar still jumps to the active tab once, without animation.
 */
export default class SotbitTabGroup extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        activeTabIndex: { type: Number, reflect: true, attribute: ATTRIBUTES.ACTIVE_TAB },
        useHistory: { type: Boolean, attribute: ATTRIBUTES.USE_HISTORY },
        disableTabBarScroll: { type: Boolean, attribute: ATTRIBUTES.DISABLE_TAB_BAR_SCROLL },
    };

    constructor() {
        super();
        this.tabs = [];
        this.panels = [];
        this.underline = null;
        this.activeTabIndex = 0;
        this.useHistory = false;
        this.disableTabBarScroll = false;
        this.tabsRef = createRef();

        this._handleSlotChangeDebonce = debounce(this._handleSlotChange.bind(this), 60, {
            maxWait: 100,
            leading: false,
            trailing: true,
        });

        this._onTabsScroll = () => this._updateUnderlineOnScroll();
        this._onTabsPointerDown = this._onTabsPointerDown.bind(this);
        this._onTabsPointerMove = this._onTabsPointerMove.bind(this);
        this._onTabsPointerEnd = this._onTabsPointerEnd.bind(this);
        this._debouncedSyncTabBarOverflow = debounce(
            () => this._syncTabBarOverflow(),
            TAB_BAR_OVERFLOW_SYNC_DEBOUNCE_MS
        );
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.hasUpdated) {
            this._attachTabBarListeners();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._detachTabBarListeners();
    }

    firstUpdated() {
        this._attachTabBarListeners();
    }

    _attachTabBarListeners() {
        if (this._tabBarListenersAttached) {
            return;
        }

        const container = this.tabsRef.value;
        if (!container) {
            return;
        }

        this._tabBarListenersAttached = true;
        container.addEventListener(EVENTS.SCROLL, this._onTabsScroll);
        container.addEventListener(EVENTS.POINTER_DOWN, this._onTabsPointerDown);
        this._refreshTabBarOverflowObservation();
        window.addEventListener('resize', this._debouncedSyncTabBarOverflow);
    }

    _detachTabBarListeners() {
        if (!this._tabBarListenersAttached) {
            return;
        }

        window.removeEventListener('resize', this._debouncedSyncTabBarOverflow);
        this._debouncedSyncTabBarOverflow.cancel();
        this._clearTabBarOverflowObservation();

        const container = this.tabsRef.value;
        if (container) {
            container.removeEventListener(EVENTS.SCROLL, this._onTabsScroll);
            this._teardownTabsDragScroll(container);
            container.classList.remove(TAB_BAR_OVERFLOW_CLASS, TAB_BAR_DRAGGING_CLASS);
        }

        this._tabBarListenersAttached = false;
        this._tabBarHasOverflow = false;
    }

    updated(changedProps) {
        if (changedProps.has('activeTabIndex') && typeof changedProps.get('activeTabIndex') === 'number') {
            this.setActiveTab(this.activeTabIndex);
        }
    }

    _handleSlotChange() {
        this.tabs = this.shadowRoot.querySelector('slot[name="tab"]').assignedElements({ flatten: true });
        this.panels = this.shadowRoot.querySelector('slot:not([name])').assignedElements({ flatten: true });

        this.tabs.forEach((tab) => {
            if (tab._handleClick) {
                tab.removeEventListener(EVENTS.CLICK, tab._handleClick);
            }
        });

        this.tabs.forEach((tab, index) => {
            tab._handleClick = () => {
                if (!tab.disabled) {
                    this.activeTabIndex = index;
                }

                if (tab.id && this.useHistory) {
                    window.history.replaceState(null, '', `#${tab.id}`);
                }
            };

            tab.addEventListener(EVENTS.CLICK, tab._handleClick);
        });

        this._refreshTabBarOverflowObservation();

        requestAnimationFrame(() => {
            try {
                const initiallyActiveIndex = this.tabs.findIndex((tab) => tab.hasAttribute('active'));
                this.activeTabIndex = initiallyActiveIndex !== -1 ? initiallyActiveIndex : this.activeTabIndex;

                if (!this.underline) {
                    this._createUnderline();
                }

                if (!this.useHistory) {
                    this.setActiveTab(this.activeTabIndex, { isInitial: true });
                    return;
                }

                for (const [index, tab] of this.tabs.entries()) {
                    if (tab.id && window.location.hash?.replace('#', '') === tab.id) {
                        this.setActiveTab(index, { isInitial: true });

                        setTimeout(() => {
                            const top = this.getBoundingClientRect().top + window.scrollY;
                            const offsetElement = document.querySelector(this.getAttribute('offset-element'));

                            let offset = 0;

                            if (offsetElement) {
                                offset = offsetElement.offsetHeight;
                            }

                            window.scrollTo({ top: top - offset, behavior: 'smooth' });
                        }, 50);
                        return;
                    }
                }

                this.setActiveTab(this.activeTabIndex, { isInitial: true });
            } finally {
                this._syncTabBarOverflow();
            }
        });
    }

    _syncTabBarOverflow() {
        const container = this.tabsRef.value;
        if (!container) {
            return;
        }

        const hasOverflow = container.scrollWidth > container.clientWidth + TAB_BAR_OVERFLOW_EPSILON_PX;
        this._tabBarHasOverflow = hasOverflow;
        container.classList.toggle(TAB_BAR_OVERFLOW_CLASS, hasOverflow);
    }

    _refreshTabBarOverflowObservation() {
        if (!this._tabBarListenersAttached) {
            return;
        }

        const container = this.tabsRef.value;
        if (!container) {
            return;
        }

        if (typeof ResizeObserver === 'undefined') {
            this._syncTabBarOverflow();
            return;
        }

        if (!this._tabBarOverflowObserver) {
            this._tabBarOverflowObserver = new ResizeObserver(() => this._debouncedSyncTabBarOverflow());
        } else {
            this._tabBarOverflowObserver.disconnect();
        }

        this._tabBarOverflowObserver.observe(this);
        this._tabBarOverflowObserver.observe(container);
        this.tabs.forEach((tab) => this._tabBarOverflowObserver.observe(tab));
        this._syncTabBarOverflow();
    }

    _clearTabBarOverflowObservation() {
        this._tabBarOverflowObserver?.disconnect();
        this._tabBarOverflowObserver = undefined;
    }

    _createUnderline() {
        if (this.underline) {
            return;
        }

        this.underline = document.createElement('div');
        this.underline.className = 'underline';
        this.shadowRoot.querySelector('.tabs').appendChild(this.underline);
    }

    _moveUnderline(tab, options = {}) {
        if (!tab || !this.underline) {
            return;
        }

        const container = this.shadowRoot.querySelector('.tabs');
        const tabRect = tab.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const TRANSITION_VALUE = 'transform 0.3s ease, width 0.3s ease';

        if (tabRect.width === 0) {
            return;
        }

        const offsetX = tabRect.left - containerRect.left + container.scrollLeft;
        const useTransition = !options.isInitial && !options.isScrolling;

        this.underline.style.transition = useTransition ? TRANSITION_VALUE : 'none';
        this.underline.style.width = `${tabRect.width}px`;
        this.underline.style.transform = `translateX(${offsetX}px)`;
    }

    setActiveTab(selectedIndex, options = {}) {
        if (!this.tabs.length && !this.panels.length) {
            return;
        }

        this.activeTabIndex = selectedIndex;

        if (this.tabs.length) {
            this.tabs.forEach((tab, index) => {
                tab.active = index === selectedIndex;
            });
        }

        if (this.panels.length) {
            this.panels.forEach((panel, index) => {
                panel.active = index === selectedIndex;
            });
        }

        const activeTab = this.tabs[selectedIndex];

        if (activeTab) {
            this._moveUnderline(activeTab, options);
            if (!this.disableTabBarScroll || options.isInitial) {
                this._scrollToTab(activeTab, options.isInitial);
            }
        }

        if (!options.isInitial) {
            const event = new CustomEvent(EVENTS.CHANGE, {
                bubbles: true,
                composed: true,
                detail: {
                    index: selectedIndex,
                    tab: activeTab,
                    name: 'tabChange',
                },
            });

            this.dispatchEvent(event);
        }

        requestAnimationFrame(() => this._syncTabBarOverflow());
    }

    _scrollToTab(tab, isInitial = false) {
        if (!this.tabsRef?.value) {
            return;
        }

        const elementLeft = tab.offsetLeft;
        const elementWidth = tab.offsetWidth;
        const containerWidth = this.tabsRef.value.clientWidth;

        const targetScrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;

        this.tabsRef.value.scrollTo({
            left: targetScrollLeft,
            behavior: isInitial ? 'auto' : 'smooth',
        });
    }

    _updateUnderlineOnScroll() {
        const activeTab = this.tabs.find((tab) => tab.active);

        if (activeTab) {
            this._moveUnderline(activeTab, { isScrolling: true });
        }
    }

    _onTabsPointerDown(e) {
        if (e.pointerType === 'mouse' && e.button !== 0) {
            return;
        }

        const container = this.tabsRef.value;
        if (!container) {
            return;
        }

        this._syncTabBarOverflow();
        if (!this._tabBarHasOverflow) {
            return;
        }

        this._dragPointerId = e.pointerId;
        this._dragStartX = e.clientX;
        this._dragStartScrollLeft = container.scrollLeft;
        this._dragExceededClickThreshold = false;
        /** Only set after threshold so `setPointerCapture` does not break link click / `onclick` on tabs. */
        this._tabBarPointerCaptureActive = false;

        container.addEventListener(EVENTS.POINTER_MOVE, this._onTabsPointerMove);
        container.addEventListener(EVENTS.POINTER_UP, this._onTabsPointerEnd);
        container.addEventListener(EVENTS.POINTER_CANCEL, this._onTabsPointerEnd);
    }

    _onTabsPointerMove(e) {
        if (e.pointerId !== this._dragPointerId) {
            return;
        }

        const container = this.tabsRef.value;
        if (!container) {
            return;
        }

        const dx = e.clientX - this._dragStartX;

        if (Math.abs(dx) > DRAG_CLICK_SUPPRESS_PX) {
            this._dragExceededClickThreshold = true;
            if (!this._tabBarPointerCaptureActive) {
                this._tabBarPointerCaptureActive = true;
                container.classList.add(TAB_BAR_DRAGGING_CLASS);
                try {
                    container.setPointerCapture(e.pointerId);
                } catch {}
            }
        }

        container.scrollLeft = this._dragStartScrollLeft - dx;
    }

    _onTabsPointerEnd(e) {
        if (e.pointerId !== this._dragPointerId) {
            return;
        }

        const container = this.tabsRef.value;

        if (container) {
            if (this._tabBarPointerCaptureActive) {
                try {
                    container.releasePointerCapture(e.pointerId);
                } catch {}
            }
            this._tabBarPointerCaptureActive = false;

            container.classList.remove(TAB_BAR_DRAGGING_CLASS);
            container.removeEventListener(EVENTS.POINTER_MOVE, this._onTabsPointerMove);
            container.removeEventListener(EVENTS.POINTER_UP, this._onTabsPointerEnd);
            container.removeEventListener(EVENTS.POINTER_CANCEL, this._onTabsPointerEnd);

            if (this._dragExceededClickThreshold) {
                const blockClick = (clickEv) => {
                    clickEv.preventDefault();
                    clickEv.stopImmediatePropagation();
                };
                container.addEventListener(EVENTS.CLICK, blockClick, { capture: true, once: true });
            }
        }

        this._dragPointerId = undefined;
    }

    _teardownTabsDragScroll(container) {
        container.removeEventListener(EVENTS.POINTER_DOWN, this._onTabsPointerDown);

        if (this._dragPointerId !== undefined && this._tabBarPointerCaptureActive) {
            try {
                container.releasePointerCapture(this._dragPointerId);
            } catch {}
        }
        this._tabBarPointerCaptureActive = false;

        container.classList.remove(TAB_BAR_DRAGGING_CLASS);
        container.removeEventListener(EVENTS.POINTER_MOVE, this._onTabsPointerMove);
        container.removeEventListener(EVENTS.POINTER_UP, this._onTabsPointerEnd);
        container.removeEventListener(EVENTS.POINTER_CANCEL, this._onTabsPointerEnd);
        this._dragPointerId = undefined;
    }

    render() {
        return html`
            <div class="tab-group" part="tab-group" @slotchange="${this._handleSlotChangeDebonce}">
                <div class="tabs" part="tabs" ${ref(this.tabsRef)}>
                    <slot name="tab"></slot>
                </div>

                <div class="panels" part="panels">
                    <slot></slot>
                </div>
            </div>
        `;
    }
}
